import styled from 'styled-components';
import { Format, StyleColor, BaseStyledComponentProps } from '../../utils/styles-constants';
import { hexToRgba } from '../../utils/style';
import { ListDisplayType } from '../../containers/DriveItemList/constants';

interface ListItemContainerProps extends BaseStyledComponentProps {
	isEditMode: boolean;
	itemsCount: number;
}

interface ListItemCardProps {
	isDraggable: boolean;
	isDragging: boolean;
	isDragPreview: boolean;
}

const gridGutter = `11px`; // Will be configurable in the admin in the future

const getEditModeLineColor = (color: string) => hexToRgba(color, 30);
const renderVerticalEditModeLine = (itemsCount: number, color: string) => `
	&::after {
		position: absolute;
		top: 0;
		bottom: 0;
		right: -7px;
		width: 1px;
		background-image: linear-gradient(0deg, ${getEditModeLineColor(color)}, ${getEditModeLineColor(color)} 33%, transparent 33%, transparent 100%);
		background-size: 1px 30px;
	}

	@media ${Format.SmartphoneLandscape}, ${Format.Tablet} {
		&::after {
			content: '';
		}

		&:nth-of-type(2n) {
			&::after {
				display: none;
			}
		}

		${itemsCount < 2 ? `
			&:last-child {
				&::after {
					display: none;
				}
			}
		` : ''}
	}

	@media ${Format.SmallDesktop} {
		&::after {
			content: '';
		}

		&:nth-of-type(3n) {
			&::after {
				display: none;
			}
		}

		${itemsCount < 3 ? `
			&:last-child {
				&::after {
					display: none;
				}
			}
		` : ''}
	}

	@media ${Format.MediumDesktop} {
		&::after {
			content: '';
		}

		&:nth-of-type(4n) {
			&::after {
				display: none;
			}
		}

		${itemsCount < 4 ? `
			&:last-child {
				&::after {
					display: none;
				}
			}
		` : ''}
	}
`;

const renderHorizontalEditModeLine = (itemsCount: number, color: string) => `
	&::before {
		content: '';
		position: absolute;
		bottom: -4px;
		left: 0;
		right: 0;
		height: 1px;
		background-image: linear-gradient(90deg, ${getEditModeLineColor(color)}, ${getEditModeLineColor(color)} 33%, transparent 33%, transparent 100%);
		background-size: 30px 1px;
	}

	&:last-child {
		&::before {
			display: none;
		}
	}

	@media ${Format.SmartphoneLandscape}, ${Format.Tablet} {
		${itemsCount % 2 === 0 ? `
			&:nth-last-child(-n+2) {
				&::before {
					display: none;
				}
			}
		` : ''}
	}

	@media ${Format.SmallDesktop} {
		${itemsCount % 3 === 0 ? `
			&:nth-last-child(-n+3) {
				&::before {
					display: none;
				}
			}
		` : ''}

		${itemsCount % 3 === 2 ? `
			&:nth-last-child(-n+2) {
				&::before {
					display: none;
				}
			}
		` : ''}
	}

	@media ${Format.MediumDesktop} {
		${itemsCount % 4 === 0 ? `
			&:nth-last-child(-n+4) {
				&::before {
					display: none;
				}
			}
		` : ''}

		${itemsCount % 4 === 3 ? `
			&:nth-last-child(-n+3) {
				&::before {
					display: none;
				}
			}
		` : ''}

		${itemsCount % 4 === 2 ? `
			&:nth-last-child(-n+2) {
				&::before {
					display: none;
				}
			}
		` : ''}
	}
`;

const renderEditModeGrid = (itemsCount: number, color: string) => `
	${renderVerticalEditModeLine(itemsCount, color)}
	${renderHorizontalEditModeLine(itemsCount, color)}
`;

const gridBreakpoints = (isEditMode: boolean, itemsCount: number, color: string) => `
	position: relative;
	margin-top: 5px; /* Will be configurable in the admin in the future */
	margin-bottom: 0;
	box-sizing: border-box;
	width: 100%;

	@media ${Format.SmartphoneLandscape}, ${Format.Tablet} {
		width: calc(50% - ${gridGutter} * 1 / 2);
		margin-right: ${gridGutter};

		&:nth-of-type(2n) {
			margin-right: 0;
		}
	}

	@media ${Format.SmallDesktop} {
		width: calc(33.3% - ${gridGutter} * 2 / 3);
		margin-right: ${gridGutter};

		&:nth-of-type(3n) {
			margin-right: 0;
		}
	}

	@media ${Format.MediumDesktop} {
		width: calc(25% - ${gridGutter} * 3 / 4);
		margin-right: ${gridGutter};

		&:nth-of-type(4n) {
			margin-right: 0;
		}
	}

	@media print {
		width: calc(50% - ${gridGutter} * 1 / 2);
	}

	${isEditMode ? renderEditModeGrid(itemsCount, color) : ''}
`;

const ListItemContainerVariants: ({}) = {
	[ListDisplayType.Grid]: styled('div')`
		${({ isEditMode, itemsCount, theme }: ListItemContainerProps) => gridBreakpoints(isEditMode, itemsCount, theme.primary.actionColor)}
	`,
	[ListDisplayType.Row]: styled('div')`
		${({ isEditMode, itemsCount, theme }: ListItemContainerProps) => gridBreakpoints(isEditMode, itemsCount, theme.primary.actionColor)}
	`,
	[ListDisplayType.Masonry]: styled('div')`
		width: 100%;
		margin-top: 5px; /* Will be configurable in the admin in the future */
		position: relative;
	`,
	[ListDisplayType.Coverflow]: styled('div')`
		width: 100%;
	`,
};

interface ListItemCardInnerProps extends BaseStyledComponentProps {
	isRowView: boolean;
}

const ListItem = styled('div')`
	cursor: ${(props: ListItemCardProps) => props.isDraggable ? 'grab' : 'pointer'};
	opacity: ${(props: ListItemCardProps) => props.isDragging ? '0.25' : '1'};
	position: relative;

	${(props: ListItemCardProps) => props.isDraggable && `
		> * {
			pointer-events: none;
		}
	`}

	${(props: ListItemCardProps) => props.isDragPreview && `
		&::after {
			content: '';
			position: absolute;
			top: 9px;
			bottom: 9px;
			left: 9px;
			right: 9px;
			z-index: 2;
			border-radius: 9px;
			background: ${hexToRgba(StyleColor.Black, 25)};
		}
	`}
`;

const ListItemCardInner = styled('div')`
	width: 100%;
	border-radius: 9px;
	${(props: ListItemCardInnerProps) => props.isRowView &&
		`display: flex;
		position: relative;
		z-index: 2;

		& > span {
			position: relative;
			z-index: 3;
		}

		&:after {
			content: '';
			position: absolute;
			width: calc(100% - 38px);
			height: 100%;
			right: 0;
			top: 0;
			z-index: 1;
			box-shadow: 0 9px 39px 1px rgba(0,0,0,0.1);
			border-radius: 9px;

			@media print {
				box-shadow: none;
			}
		}`
	}

	@media print {
		break-inside: avoid;
	}
`;

const listItemBlockStyle = (props: BaseStyledComponentProps) => `
	@media ${Format.SmartphonePortrait} {
		margin: 7.5px 0;
	}
`;

const ListItemCardVariants: ({}) = {
	[ListDisplayType.Grid]: styled(ListItem)`
		${(props: BaseStyledComponentProps) => listItemBlockStyle(props)}
		padding: 9px;
		box-sizing: border-box;
		background-color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 4)};
		border-radius: 9px;
	`,
	[ListDisplayType.Row]: styled(ListItem)`
		background-color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 4)};
		height: auto;
		border-radius: 16px;
		padding: 9px;
		box-sizing: border-box;
	`,
	[ListDisplayType.Masonry]: styled(ListItem)`
		${(props: BaseStyledComponentProps) => listItemBlockStyle(props)}
		box-sizing: border-box;
		padding: 9px;
		border-radius: 9px;
		background-color: ${(props: BaseStyledComponentProps) => hexToRgba(props.theme.primary.strongColor, 4)};
	`,
	[ListDisplayType.Coverflow]: styled(ListItem)`
		${(props: BaseStyledComponentProps) => listItemBlockStyle(props)}
	`,
};

const ListItemComponentVariants: ({}) = {
	[ListDisplayType.Grid]: styled(ListItem)`
		${(props: BaseStyledComponentProps) => listItemBlockStyle(props)}
		padding: 9px; /* this padding matches the card border to prevent displacement */
		box-sizing: border-box;
	`,
	[ListDisplayType.Row]: styled(ListItem)`
		padding: 9px;/* this padding matches the card border to prevent displacement */
		box-sizing: border-box;
		height: auto;
		border-radius: 9px;
	`,
	[ListDisplayType.Masonry]: styled(ListItem)`
		${(props: BaseStyledComponentProps) => listItemBlockStyle(props)}
		padding: 9px;/* this padding matches the card border to prevent displacement */
	`,
	[ListDisplayType.Coverflow]: styled(ListItem)`
		background-color: ${StyleColor.White};
		border-radius: 9px;
	`,
};

const badgesize = '8px';

const ListItemUnreadBadge = styled('div')`
	width: ${badgesize};
	height: ${badgesize};
	background-color: ${(props: BaseStyledComponentProps) => props.theme.primary.actionColor};
	position: absolute;
	left: -5px; /* will need to be recalculated when gutters will be configurable */
	top: 12px; /* will need to be recalculated when gutters will be configurable */
	border-radius: 7px;
`;

export {
	ListItemContainerVariants,
	ListItemComponentVariants,
	ListItemCardVariants,
	ListItemUnreadBadge,
	ListItemCardInner
};


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/components/ListItem/styles.ts